package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.*
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

data class LeaderEntry(
    // Participant may be null when a stat row references a player that's
    // since been removed from the event roster — the card still renders
    // with whatever data the participant DTO would have supplied, using
    // generic placeholders for the missing fields (name, avatar, rating).
    val userId: String,
    val participant: EventParticipantDto?,
    val value: Int,
    val rank: Int
)

// Per-sport stat keys for the two leaderboard panels. Pro events
// surface the scoring + playmaking stat for team sports, and the
// games-won + sets-won progress for tennis/padel — falling back to
// whatever the backend orders first when the sport isn't recognised.
internal val LeaderStatKeysBySport: Map<String, Pair<String, String>> = mapOf(
    "football" to ("goals" to "assists"),
    "basketball" to ("points" to "assists"),
    "volleyball" to ("kills" to "assists"),
    "tennis" to ("games" to "sets_won"),
    "padel" to ("games" to "sets_won"),
)

// Figma 8924-346033 / 8924-346992: 4 cards per row, 2 rows visible, the
// rest scroll. The Figma artboard is 638px wide but in the live layout
// the panel is ~420–450px (left column shared between MatchInfo banner +
// leaders), so card width is responsive — each cell is `1fr` of the
// panel and the card preserves the Arena card aspect ratio (94:147), so
// the stat-tile percentage box lines up with the FIFA template notch the
// way the live MatchView card does.
private const val LEADER_GRID_COLS = 4
private const val LEADER_VISIBLE_ROWS = 2
private const val LEADER_GRID_COL_GAP = 12
private const val LEADER_GRID_ROW_GAP = 12
private val LEADER_CARD_RATIO_W = PlayerCardSize.Arena.w
private val LEADER_CARD_RATIO_H = PlayerCardSize.Arena.h
private const val PLAYER_KIT_CDN_BASE = "https://images.arenaproplay.com/"

@Composable
fun PerformanceLeaders(
    event: EventDetailDto,
    playerStats: EventPlayerStatsResultDto?,
    onViewProfile: (userId: String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    if (playerStats == null) return

    val t = LocalAppStrings.current
    // Get PLAYER-scoped stat types, sorted by display order
    val playerStatTypes = playerStats.statTypes
        .filter { it.scope.uppercase() == "PLAYER" }
        .sortedBy { it.displayOrder }

    val sportKey = event.sportName.lowercase()
    val preferredKeys = LeaderStatKeysBySport[sportKey]
    val statType1 = preferredKeys
        ?.let { (key, _) -> playerStatTypes.find { it.displayKey == key } }
        ?: playerStatTypes.getOrNull(0)
    val statType2 = preferredKeys
        ?.let { (_, key) -> playerStatTypes.find { it.displayKey == key } }
        ?: playerStatTypes.getOrNull(1)

    if (statType1 == null) return

    // Racket sports use the won-games / won-sets pair; the football-themed
    // "match-winning pass" copy and sight icon don't fit, so swap to a
    // trophy + neutral message there.
    val isRacketSport = sportKey == "tennis" || sportKey == "padel"
    val rightPanelStatIcon = if (isRacketSport) AppIcons.Trophy else AppIcons.Sight
    val leftEmptyMessage = if (isRacketSport)
        t.eventLeadersEmptyWinGame
    else
        t.eventLeadersEmptyFirstScore
    val rightEmptyMessage = if (isRacketSport)
        t.eventLeadersEmptyTakeSet
    else
        t.eventLeadersEmptyFirstAssist

    Row(
        modifier = modifier.fillMaxWidth().sGap(20)
    ) {
        // Left panel: first stat type (e.g. Top Strikers)
        LeaderPanel(
            title = t.eventTopStat(statType1.name),
            icon = AppIcons.Trophy,
            statIcon = AppIcons.ballSmall(event.sportName),
            entries = buildLeaderEntries(statType1, playerStats, event),
            sport = event.sportName,
            teams = event.teams,
            backgroundGradient = "linear-gradient(17.1deg, rgba(0,59,174,0.03) 26.9%, rgba(3,87,230,0.03) 67.5%)",
            emptyIcon = AppIcons.EmptyTopStrikers,
            emptyMessage = leftEmptyMessage,
            onViewProfile = onViewProfile,
            modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
        )

        // Right panel: second stat type (e.g. Top Assistants)
        if (statType2 != null) {
            LeaderPanel(
                title = t.eventTopStat(statType2.name),
                icon = AppIcons.Lightning,
                statIcon = rightPanelStatIcon,
                entries = buildLeaderEntries(statType2, playerStats, event),
                sport = event.sportName,
                teams = event.teams,
                backgroundGradient = "linear-gradient(-27.4deg, rgba(20,26,32,0.12) 7.2%, rgba(10,36,68,0.2) 95.1%)",
                emptyIcon = AppIcons.EmptyTopAssistants,
                emptyMessage = rightEmptyMessage,
                onViewProfile = onViewProfile,
                modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
            )
        }
    }
}

internal fun buildLeaderEntries(
    statType: SportStatTypeDto,
    playerStats: EventPlayerStatsResultDto,
    event: EventDetailDto
): List<LeaderEntry> {
    val sorted = playerStats.playerStats
        .filter { it.statTypeId == statType.id && it.value > 0 }
        .sortedByDescending { it.value }

    // Olympic-style ranking: equal values share a place, the next place
    // skips the tied count (3, 3, 1 → ranks 1, 1, 3).
    var currentRank = 0
    var previousValue: Int? = null
    return sorted.mapIndexed { index, stat ->
        if (stat.value != previousValue) {
            currentRank = index + 1
            previousValue = stat.value
        }
        // Participant lookup is best-effort — if the player has been
        // removed from the roster the card still renders with placeholder
        // identity fields rather than silently disappearing.
        val participant = event.participants.find { it.userId == stat.userId }
        if (participant == null) {
            console.warn(
                "PerformanceLeaders: stat for userId=${stat.userId} has no " +
                    "matching event.participant — rendering with placeholders"
            )
        }
        LeaderEntry(
            userId = stat.userId,
            participant = participant,
            value = stat.value,
            rank = currentRank,
        )
    }
}

@Composable
private fun LeaderPanel(
    title: String,
    icon: String,
    statIcon: String,
    entries: List<LeaderEntry>,
    sport: String,
    teams: List<EventTeamDto>,
    backgroundGradient: String,
    emptyIcon: String,
    emptyMessage: String,
    onViewProfile: (userId: String) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxHeight()
            .sBorderRadius(40)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("padding", "${s(16)} ${s(16)} ${s(18)} ${s(16)}")
                property("border", "1px solid rgba(41,52,138,0.2)")
                property("background", backgroundGradient)
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Header: title + icon
        Row(
            modifier = Modifier.fillMaxWidth().sPaddingLeft(8).sGap(24),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(FontWeight.Medium)
                    .sLineHeight(32)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("letter-spacing", s(0.24)) }
            )
            Icon(src = icon, modifier = Modifier.sSize(24))
        }

        if (entries.isEmpty()) {
            // Empty state: illustration + message, centered in remaining space
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .sGap(24)
                    .sPaddingBottom(28),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Bottom
            ) {
                Image(
                    src = emptyIcon,
                    modifier = Modifier
                        .sHeight(190)
                        .styleModifier { property("object-fit", "contain") }
                )
                SpanText(
                    emptyMessage,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(22)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("letter-spacing", s(0.09))
                            property("text-align", "center")
                        }
                )
            }
        } else {
            // 4-col grid; cards fill their cells and lock to the FIFA card's
            // 119:187 aspect ratio. The grid is constrained to the height of
            // exactly two rows (computed from the panel width via container
            // queries) and additional rows scroll vertically inside.
            //
            // max-height math: each cell is `(100cqw − 3 gaps) / 4` wide; card
            // height is that width × 187/119; two rows + one row-gap gives the
            // visible viewport.
            val colGap = LEADER_GRID_COL_GAP
            val rowGap = LEADER_GRID_ROW_GAP
            val maxHeight =
                "calc((100cqw - ${LEADER_GRID_COLS - 1} * ${colGap}px) / " +
                    "${LEADER_GRID_COLS} * ${LEADER_CARD_RATIO_H} / ${LEADER_CARD_RATIO_W} * " +
                    "${LEADER_VISIBLE_ROWS} + ${(LEADER_VISIBLE_ROWS - 1) * rowGap}px)"
            // Kobweb's Box is itself a single-cell CSS grid (used for
            // layering children), so wrapping the leaderboard grid in a
            // Box would stack the cards on top of each other regardless
            // of `grid-template-columns`. Raw `Div`s sidestep that and
            // lay the cards out across the four columns as intended.
            Div(attrs = {
                style {
                    width(100.percent)
                    property("margin-top", s(8))
                    property("container-type", "inline-size")
                    // Grow into whatever vertical space the panel has left
                    // below the header — paired with `min-height: 0` so the
                    // child grid's `max-height` cap (= exactly two card
                    // rows) actually clips when the panel is short, instead
                    // of letting the grid push past the panel edge.
                    property("flex", "1 1 0")
                    property("min-height", "0")
                }
            }) {
                Div(attrs = {
                    style {
                        width(100.percent)
                        property("display", "grid")
                        property("grid-template-columns", "repeat(${LEADER_GRID_COLS}, 1fr)")
                        property("column-gap", "${colGap}px")
                        property("row-gap", "${rowGap}px")
                        property("max-height", maxHeight)
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(60,121,241,0.4) transparent")
                    }
                }) {
                    entries.forEach { entry ->
                        LeaderCard(
                            entry = entry,
                            statIcon = statIcon,
                            sport = sport,
                            teams = teams,
                            onViewProfile = onViewProfile,
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun LeaderCard(
    entry: LeaderEntry,
    statIcon: String,
    sport: String,
    teams: List<EventTeamDto>,
    onViewProfile: (userId: String) -> Unit,
) {
    val t = LocalAppStrings.current
    val p = entry.participant
    val playerName = (p?.fullName?.split(" ")?.lastOrNull() ?: p?.username ?: t.eventPlayerFallback)
        .uppercase()
    val rating = p?.overallRating?.toInt() ?: 0
    val position = p?.position ?: ""
    val avatarUrl = p?.cardAvatarUrl ?: AppIcons.EmptyAvatar
    val cardAssetsBasePath = p?.cardAssetsBasePath
    val cardTextColor = p?.cardTextColor
    val statTileAssetsBasePath = p?.cardAssetsBasePath ?: "cards/football"
    // Resolve the player's event team so the card can surface its crest /
    // colour badge under the stat tile.
    val team = p?.eventTeamId?.let { teamId -> teams.find { it.id == teamId } }

    // Responsive outer shell — fills its grid cell at the FIFA card's
    // aspect ratio. Container queries let the inner fixed-pixel box scale
    // proportionally to the cell.
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onViewProfile(entry.userId) }
            .styleModifier {
                property("aspect-ratio", "${PlayerCardSize.Arena.w} / ${PlayerCardSize.Arena.h}")
                property("container-type", "size")
            }
    ) {
        // Fixed-pixel inner box matching the Arena design grid (94×147),
        // scaled via CSS transform to fit whatever size the outer cell
        // resolves to. This mirrors the live `PlayerCardKit` structure
        // from MatchView, so `right:5% / 22%×28%` on the stat tile lands
        // on the same FIFA template notch it was designed for — without
        // `fillContainer` on PlayerCard, which would silo the scaling
        // inside PlayerCard and leave the sibling StatTile sized against
        // the resized outer cell instead of the design grid.
        Box(
            modifier = Modifier
                .sWidth(PlayerCardSize.Arena.w)
                .sHeight(PlayerCardSize.Arena.h)
                .position(Position.Relative)
                .styleModifier {
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "50%")
                    property(
                        "transform",
                        "translate(-50%, -50%) scale(min(" +
                            "calc(100cqw / ${PlayerCardSize.Arena.w}px), " +
                            "calc(100cqh / ${PlayerCardSize.Arena.h}px)" +
                            "))",
                    )
                    property("transform-origin", "center")
                }
        ) {
            // FIFA-style card art with player avatar + name + rating
            // (bottom-centre `card_rating.png` badge) baked in.
            PlayerCard(
                playerName = playerName,
                rating = rating,
                position = position,
                sport = sport,
                size = PlayerCardSize.Arena,
                avatarUrl = avatarUrl,
                cardAssetsBasePath = cardAssetsBasePath,
                cardTextColor = cardTextColor,
            )

            // Stat tile sits at the same percentage box as the live
            // MatchView `StatBlock` (Figma 7904:364601) — but now relative
            // to the 94×147 design grid, so the percentages line up with
            // the card template notch instead of drifting outside it.
            LeaderStatTile(
                icon = statIcon,
                value = entry.value,
                cardAssetsBasePath = statTileAssetsBasePath,
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "18%")
                        property("right", "5%")
                        property("width", "22%")
                        property("height", "28%")
                        property("z-index", "5")
                    }
            )

            // Team badge directly under the stat tile (which spans
            // top 18% → 46%). Sits flush below the tile and shares the
            // same right column (right:5% / width:22%), so it visually
            // chains with the stat as one stack rather than floating off
            // to the side of the card.
            if (team != null) {
                LeaderTeamBadge(
                    team = team,
                    modifier = Modifier
                        .position(Position.Absolute)
                        .styleModifier {
                            property("top", "46%")
                            property("right", "8%")
                            property("width", "22%")
                            property("height", "14%")
                            property("z-index", "5")
                        }
                )
            }
        }
    }
}

/**
 * Static stat tile rendered on top-right of a leaderboard card. Visual
 * mirror of the live `StatBlock` (Figma 7904:364601) but without the
 * admin +/- stepper popout — leaders are read-only.
 */
@Composable
private fun LeaderStatTile(
    icon: String,
    value: Int,
    cardAssetsBasePath: String,
    modifier: Modifier = Modifier,
) {
    val blockUrl = "${PLAYER_KIT_CDN_BASE}${cardAssetsBasePath}/stat.png"
    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center,
    ) {
        // `stat.png` frame from R2 — mirrored horizontally so its notch
        // faces the card centre (matches the live MatchView's right-tile
        // orientation). Fixed pixel sizes inside are scaled along with
        // the whole 94×147 design grid by the CSS transform set on the
        // LeaderCard wrapper.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("background", "url('$blockUrl') center/contain no-repeat")
                    property("transform", "scaleX(-1)")
                },
            contentAlignment = Alignment.Center,
        ) {
            // Inner content un-flipped so the icon/digit read upright.
            // Vertical offset nudges the content down off the tile's top
            // notch; divider gets a touch of breathing room top + bottom
            // via its own margin so the icon stays close to the top edge.
            Column(
                modifier = Modifier
                    .sMarginTop(3)
                    .styleModifier { property("transform", "scaleX(-1)") },
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Image(
                    src = icon,
                    modifier = Modifier.sSize(9),
                )
                Box(
                    modifier = Modifier
                        .sWidth(11)
                        .sHeight(0.5)
                        .sMarginTop(2)
                        .sMarginBottom(2)
                        .styleModifier {
                            property("background-color", "rgba(190, 214, 245, 0.5)")
                        }
                )
                SpanText(
                    value.toString(),
                    modifier = Modifier
                        .sMarginTop(-1)
                        .sFontSize(11)
                        .fontWeight(FontWeight.SemiBold)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("line-height", s(12)) },
                )
            }
        }
    }
}

/**
 * Small team marker rendered under the stat tile. Visual mirror of
 * `MatchPerformance.TeamScoreColumn` — official crest when uploaded,
 * otherwise the same coloured disc with `team-card-texture.png` +
 * `team-card-glow.svg` + centered `TeamPeople` icon used on the
 * scoreboard, just scaled down.
 */
@Composable
internal fun LeaderTeamBadge(
    team: EventTeamDto,
    modifier: Modifier = Modifier,
) {
    val logo = team.officialTeamImageUrl
    if (logo != null) {
        Image(
            src = logo,
            modifier = modifier.styleModifier {
                property("object-fit", "contain")
            },
        )
    } else {
        val teamBg = team.color ?: "var(--l-s3, #03051F)"
        Box(
            // Note: do NOT chain `.position(Position.Relative)` here — the
            // caller's modifier already sets `position: absolute` (so the
            // badge anchors to the stat tile's column via `right:5%`).
            // Adding `relative` would clobber that, drop the badge out of
            // absolute positioning and into normal flow → it lands at the
            // top-left of the card instead of under the stat tile. The
            // already-`absolute` element is its own positioning context
            // for the children below.
            modifier = modifier
                .sBorderRadius(100)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("border", "0.583px solid #272E32")
                    property("background", teamBg)
                },
            contentAlignment = Alignment.Center,
        ) {
            Image(
                src = "/images/team-card-texture.png",
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .fillMaxSize()
                    .styleModifier {
                        property("opacity", "0.55")
                        property("object-fit", "cover")
                        property("pointer-events", "none")
                    }
            )
            Image(
                src = "/images/team-card-glow.svg",
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        // Glow spills 60% past the badge on every side —
                        // same ratio as MatchPerformance's 140px glow on
                        // an 88px badge (offset −35).
                        property("width", "160%")
                        property("height", "160%")
                        property("left", "-30%")
                        property("top", "-30%")
                        property("pointer-events", "none")
                    }
            )
            Icon(
                src = AppIcons.TeamPeople,
                modifier = Modifier
                    .zIndex(1)
                    .styleModifier {
                        // MatchPerformance uses a 43-px icon on an 88-px
                        // badge (≈49%); keep that proportion at any size.
                        property("width", "50%")
                        property("height", "50%")
                    }
            )
        }
    }
}
